import { Form, Row, Col, Input, message, Radio, RadioChangeEvent, Select } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { handleFetchRequest, handleUpdateRequest } from "../../api/requests";
import { AuthContext } from "../../contexts/AuthContext";
import logo from "../../assets/images/blue_logo.png";
import { Link, useNavigate, useParams } from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import { CheckCircleFilled, DeleteOutlined, LeftOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { Printer } from "lucide-react";
import { handleFetchUnits } from "../../api/units";
import getBase64 from "../../utils/getBase64";

const adminAccess = [9];

export default function EditRequest() {
  const { user, signout } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [value, setValue] = useState("High");
  const [businessUnit, setBusinessUnit] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [requesterId, setRequesterId] = useState<string | null>(null);

  const onChange = (e: RadioChangeEvent) => {
    setValue(e.target.value);
  };

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    onBeforePrint: async () => {
      form.setFieldsValue({
        requestedBy: "",
        contactEmail: "",
        priority: undefined,
      });
    },
    onAfterPrint: async () => {
      if (!request) return;
      const businessUnitId = request?.businessUnit?._id ?? request?.businessUnit;
      const requester =
        typeof request?.requestedBy === "object" && request?.requestedBy?.name
          ? request.requestedBy
          : null;
      form.setFieldsValue({
        requestedBy: requester?.name ?? user?.name ?? "",
        contactEmail: request?.contactEmail ?? requester?.email ?? user?.email ?? "",
        priority: request?.priority || "High",
        businessUnitRef: businessUnitId,
      });
    },
  });

  const { data: request, isLoading: isRequestLoading } = useQuery({
    queryKey: ["requests", id],
    queryFn: () => handleFetchRequest({ _id: id }),
    enabled: !!id,
  });

  const canEdit =
    request?.approvalStatus === "Pending" &&
    (request?.requestedBy?._id === user?._id || adminAccess.includes(user?.accessLevel));

  const { mutate, isPending } = useMutation({
    mutationFn: handleUpdateRequest,
    onSuccess: () => {
      message.success("Updated Successfully");
      setSelectedFiles([]);
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      navigate(`/change/requests/${id}/details`);
    },
    onError: (error) => {
      message.error(`${error}`);
    },
  });

  const { data: units } = useQuery({
    queryKey: ["units"],
    queryFn: handleFetchUnits,
  });

  const unitOptions = units?.records?.map((model: any, index: number) => (
    <Select.Option value={model?._id} key={index}>
      {model?.name}
    </Select.Option>
  ));

  const handleBusinessUnitChange = (value: any) => {
    const unit = units?.records?.find((model: any) => model?._id === value);
    setBusinessUnit(unit || null);
    form.setFieldsValue({
      businessUnitRef: value,
    });
  };

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    setSelectedFiles(files);
  };

  const onSubmit = async () => {
    const values = await form.getFieldsValue();

    if (!user?._id) {
      message.error("Please login to continue");
      signout();
      return;
    }

    const newAttachments = selectedFiles.length
      ? await Promise.all(selectedFiles.map((file) => getBase64(file)))
      : [];

    const selectedUnit =
      businessUnit ||
      units?.records?.find((model: any) => model?._id === values.businessUnitRef);

    const data = {
      ...values,
      requestedBy: requesterId ?? user?._id,
      businessUnit: selectedUnit?._id || values.businessUnitRef,
      businessUnitRef: selectedUnit?._id || values.businessUnitRef,
      attachments: [...existingAttachments, ...newAttachments],
    };

    await mutate({ _id: id, data });
  };

  useEffect(() => {
    if (!request || !units?.records) return;

    const attachments = request?.attachments?.length
      ? request.attachments
      : request?.thumbnail
        ? [request.thumbnail]
        : [];

    const businessUnitId = request?.businessUnit?._id ?? request?.businessUnit;
    const unit =
      units.records.find((model: any) => model?._id === businessUnitId) ||
      (typeof request?.businessUnit === "object" ? request.businessUnit : null);

    const requester =
      typeof request?.requestedBy === "object" && request?.requestedBy?.name
        ? request.requestedBy
        : null;

    setExistingAttachments(attachments);
    setBusinessUnit(unit);
    setRequesterId(requester?._id ?? user?._id ?? null);
    setValue(request?.priority || "High");
    form.setFieldsValue({
      projectName: request?.projectName,
      businessUnitRef: businessUnitId,
      requestedBy: requester?.name ?? user?.name ?? "",
      contactEmail: request?.contactEmail ?? requester?.email ?? user?.email ?? "",
      priority: request?.priority || "High",
      description: request?.description,
      reason: request?.reason,
      scope: request?.scope,
      deliverables: request?.deliverables,
      timeline: request?.timeline,
      cost: request?.cost,
      resources: request?.resources,
      stakeholders: request?.stakeholders,
      riskIdentification: request?.riskIdentification,
      riskMitigation: request?.riskMitigation,
    });
  }, [form, request, units?.records, user?._id, user?.name, user?.email]);

  const isImageAttachment = (url: string) => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);

  if (isRequestLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        Loading request...
      </div>
    );
  }

  if (request && !canEdit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white p-4">
        <p className="text-gray-700 mb-4">This request can no longer be edited.</p>
        <Link to={`/change/requests/${id}/details`} className="text-blue-600 underline">
          Back to details
        </Link>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }

          /* Hide placeholders on print */
          .print-compact input::placeholder,
          .print-compact textarea::placeholder {
            color: transparent !important;
          }

          /* Ant Design internal placeholder */
          .print-compact .ant-input::placeholder,
          .print-compact .ant-input-affix-wrapper input::placeholder {
            color: transparent !important;
          }

          /* Force 2-column grid on print */
          .print-compact .ant-row {
            display: flex !important;
            flex-wrap: wrap !important;
            margin-bottom: 0 !important;
          }
          .print-compact .ant-col-xs-24 {
            flex: 0 0 50% !important;
            max-width: 50% !important;
          }
          .print-compact .ant-col-lg-24 {
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }

          .print-compact .ant-form-item {
            margin-bottom: 6px !important;
          }
          .print-compact .ant-form-item-label {
            padding-bottom: 0 !important;
            line-height: 1.4 !important;
          }
          .print-compact .ant-form-item-label > label {
            font-size: 13px !important;
            height: auto !important;
            font-weight: 600 !important;
          }
          .print-compact input,
          .print-compact textarea {
            font-size: 13px !important;
            min-height: unset !important;
            height: 30px !important;
            padding: 4px 8px !important;
            border-radius: 0 !important;
          }
          .print-compact textarea {
            height: 52px !important;
            resize: none !important;
          }
          .print-compact .ant-radio-wrapper {
            font-size: 13px !important;
          }
          .print-compact .ant-select-selector {
            font-size: 13px !important;
            height: 30px !important;
            padding: 0 8px !important;
          }
          .print-compact .ant-col {
            padding-left: 4px !important;
            padding-right: 4px !important;
          }
          .print-header img {
            width: 80px !important;
          }
          .print-header .form-title {
            font-size: 14px !important;
            padding: 4px 0 !important;
          }
          .print-wrapper {
            padding: 12px !important;
          }

          /* Approval section */
          .approval-section {
            margin-top: 32px !important;
            border-top: 1px solid #000 !important;
            padding-top: 16px !important;
          }
          .approval-section .approval-title {
            font-size: 13px !important;
            font-weight: 700 !important;
            margin-bottom: 16px !important;
          }
          .approval-grid {
            display: flex !important;
            gap: 24px !important;
          }
          .approval-item {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 6px !important;
          }
          .approval-item .approval-label {
            font-size: 11px !important;
            font-weight: 600 !important;
            color: #333 !important;
          }
          .approval-item .approval-line {
            border-bottom: 1px solid #000 !important;
            height: 32px !important;
            width: 100% !important;
          }
        }

        /* Hidden on screen */
        .print-only { display: none; }
      `}</style>

      <div className="flex flex-col border-b pb-2 mb-2 bg-white min-h-screen">
        <div className="flex border-y no-print justify-between items-center">
          <Link
            to={`/change/requests/${id}/details`}
            className="flex items-center justify-center p-2"
          >
            <LeftOutlined /> Back
          </Link>
          <div className="flex justify-end items-center">
            <button
              type="button"
              onClick={reactToPrintFn}
              className="flex ml-2 border bg-blue-100 hover:bg-blue-200 hover:border-blue-600 justify-center items-center px-2 py-1 text-xxs gap-x-2"
            >
              <Printer size={16} color="#424242" /> Print Form
            </button>
          </div>
        </div>

        <div className="m-2 flex justify-center items-center">
          <div className="border shadow-lg my-2 bg-white w-4/5 sm:w-4/5 md:w-2/4 print:w-full">
            <div className="print-wrapper" ref={contentRef as any}>

              <div className="print-header font-bold text-2xl p-4 text-center">
                <div className="flex justify-center items-center py-2">
                  <img src={logo} alt="logo" className="w-48" />
                </div>
                <div className="form-title text-2xl font-bold">
                  Edit Change Request Form
                </div>
              </div>

              <div className="px-6 py-2">
                <Form
                  id="createUser"
                  form={form}
                  name="normal_login"
                  className="login-form print-compact"
                  onFinish={onSubmit}
                  layout="vertical"
                >
                  {/* Row 1: Project Name & Business Unit */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="projectName"
                        label={<span style={{ fontWeight: "bold" }}>Project Name</span>}
                        rules={[{ required: true, message: "Please enter project name" }]}
                      >
                        <Input
                          type="text"
                          placeholder="Enter project name"
                          style={{ borderRadius: 0, height: "35px", marginTop: "-10px" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="businessUnitRef"
                        label={<span style={{ fontWeight: "bold" }}>Business Unit</span>}
                        rules={[{ required: true, message: "Please select business unit" }]}
                      >
                        <Select
                          showSearch
                          placeholder="Business Unit"
                          optionFilterProp="children"
                          filterOption={(input: any, option: any) =>
                            option?.children
                              ?.toLowerCase()
                              .indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={handleBusinessUnitChange}
                        >
                          {unitOptions}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Row 2: Requested By & Contact Information */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="requestedBy"
                        label={<span style={{ fontWeight: "bold" }}>Requested By</span>}
                        rules={[{ required: true, message: "Please enter requester name" }]}
                      >
                        <Input
                          type="text"
                          placeholder="Enter requester name"
                          style={{ borderRadius: 0, height: "35px", marginTop: "-10px" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="contactEmail"
                        label={<span style={{ fontWeight: "bold" }}>Contact Information</span>}
                        rules={[{ required: true, message: "Please enter contact email" }]}
                      >
                        <Input
                          type="text"
                          placeholder="Enter email address"
                          style={{ borderRadius: 0, height: "35px", marginTop: "-10px" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Row 3: Priority */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="priority"
                        label={<span style={{ fontWeight: "bold" }}>Priority</span>}
                        rules={[{ required: true, message: "Please select priority" }]}
                      >
                        <Radio.Group
                          onChange={onChange}
                          value={value}
                          options={[
                            { value: "High", label: "High" },
                            { value: "Medium", label: "Medium" },
                            { value: "Low", label: "Low" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Description */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item
                        name="description"
                        label={<span style={{ fontWeight: "bold" }}>Description of Change</span>}
                        rules={[{ required: true, message: "Please enter description" }]}
                      >
                        <TextArea placeholder="Enter Description" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Reason */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item
                        name="reason"
                        label={<span style={{ fontWeight: "bold" }}>Reason for Change</span>}
                        rules={[{ required: true, message: "Please enter reason" }]}
                      >
                        <TextArea placeholder="Enter reason" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item
                        name="scope"
                        label={<span style={{ fontWeight: "bold" }}>Scope</span>}
                        rules={[{ required: true, message: "Please enter scope" }]}
                      >
                        <TextArea placeholder="Enter scope" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Timeline & Deliverables */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="timeline"
                        label={<span style={{ fontWeight: "bold" }}>Timeline</span>}
                      >
                        <Input placeholder="Enter timeline" />
                      </Form.Item>
                    </Col>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="deliverables"
                        label={<span style={{ fontWeight: "bold" }}>Deliverables</span>}
                      >
                        <Input
                          type="text"
                          placeholder="Enter deliverables"
                          style={{ borderRadius: 0, height: "35px", marginTop: "-10px" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Cost & Resources */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="cost"
                        label={<span style={{ fontWeight: "bold" }}>Cost</span>}
                      >
                        <Input
                          type="text"
                          placeholder="Enter cost"
                          style={{ borderRadius: 0, height: "35px", marginTop: "-10px" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                      <Form.Item
                        name="resources"
                        label={<span style={{ fontWeight: "bold" }}>Resources</span>}
                      >
                        <Input
                          type="text"
                          placeholder="Enter resources"
                          style={{ borderRadius: 0, height: "35px", marginTop: "-10px" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Stakeholders */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item
                        name="stakeholders"
                        label={<span style={{ fontWeight: "bold" }}>Stakeholders</span>}
                      >
                        <TextArea placeholder="Enter stakeholders" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Risk Identification */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item
                        name="riskIdentification"
                        label={<span style={{ fontWeight: "bold" }}>Risk Identification</span>}
                      >
                        <TextArea placeholder="Enter risk identification" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Risk Mitigation */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item
                        name="riskMitigation"
                        label={<span style={{ fontWeight: "bold" }}>Risk Mitigation Strategies</span>}
                      >
                        <TextArea placeholder="Enter mitigation strategies" />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Attachments */}
                  <Row gutter={[16, 0]}>
                    <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                      <Form.Item name="attachments" label={<span style={{ fontWeight: "bold" }}>Attachments (Optional)</span>}>
                        <div className="flex flex-col border border-gray-300 p-3 gap-3">
                          {existingAttachments.length > 0 && (
                            <div className="flex flex-col gap-2">
                              <span className="text-sm font-semibold text-gray-700">Current attachments</span>
                              {existingAttachments.map((attachment, index) => (
                                <div
                                  key={`${attachment}-${index}`}
                                  className="flex items-center justify-between border border-gray-200 p-2"
                                >
                                  {isImageAttachment(attachment) ? (
                                    <img src={attachment} alt={`Attachment ${index + 1}`} className="max-h-16 object-contain" />
                                  ) : (
                                    <span className="text-sm text-gray-700">Document {index + 1}</span>
                                  )}
                                  <button
                                    type="button"
                                    className="text-red-500 hover:text-red-700 flex items-center"
                                    onClick={() =>
                                      setExistingAttachments((prev) =>
                                        prev.filter((_, itemIndex) => itemIndex !== index)
                                      )
                                    }
                                  >
                                    <DeleteOutlined />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {selectedFiles.length > 0 ? (
                            <div className="flex flex-col">
                              <div className="flex items-center text-green-700">
                                <CheckCircleFilled />
                                <span className="ml-2 text-sm">
                                  {selectedFiles.length} new attachment{selectedFiles.length > 1 ? "s" : ""} selected
                                </span>
                              </div>
                              <div className="mt-2 text-xs text-gray-600">
                                {selectedFiles.map((file) => file.name).join(", ")}
                              </div>
                              <button
                                type="button"
                                className="mt-2 text-red-500 hover:text-red-700 w-fit flex items-center"
                                onClick={() => {
                                  form.setFieldsValue({ attachments: null });
                                  setSelectedFiles([]);
                                }}
                              >
                                <DeleteOutlined />
                                <span className="ml-1">Remove new attachments</span>
                              </button>
                            </div>
                          ) : (
                            <Input
                              type="file"
                              multiple
                              onChange={handleAttachmentChange}
                            />
                          )}
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Submit — hidden on print */}
                  <div className="no-print">
                    <button className="bg-karimjee hover:bg-karimjee-dark text-white font-bold py-2 px-4 rounded w-full mb-8 mt-4">
                      {isPending ? "..." : "Update"}
                    </button>
                  </div>

                  {/* Approval section — print only */}
                  <div className="print-only approval-section">
                    <div className="approval-grid">
                      <div className="approval-item">
                        <span className="approval-label">Approved By</span>
                        <div className="approval-line" />
                      </div>
                      <div className="approval-item">
                        <span className="approval-label">Date</span>
                        <div className="approval-line" />
                      </div>
                      <div className="approval-item">
                        <span className="approval-label">Signature</span>
                        <div className="approval-line" />
                      </div>
                    </div>
                  </div>

                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}